import { SupplierInput } from "@/app/Suppliers/Components/SupplierInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { FormDivider } from "@/lib/Components/Form/Components/FormDivider";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { CreateSupplierOfficeInput } from "@/gql/graphql";
import { RichTextInput } from "@/lib/Components/Form/Inputs/RichTextInput";
import { AddressInput } from "@/app/Address/Components/AddressInput";
import { useTranslations } from "use-intl";
import { CityInput } from "@/app/Cities/Cities/Components/CityInput";
import { CityListItem } from "@/app/Cities/Cities/GraphQL/cityListQuery";
import { SupplierCityCreateDialog } from "@/app/Cities/Cities/Components/SupplierCityCreateDialog";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { Button } from "@/components/catalyst/button";
import { distanceKm } from "@/lib/Geo/distanceKm";
import { TriangleAlertIcon } from "lucide-react";
import { useState } from "react";

const MAX_OFFICE_CITY_DISTANCE_KM = 50;

export function SupplierOfficeForm({
  initialLat,
  initialLng,
}: {
  initialLat?: number | null;
  initialLng?: number | null;
}) {
  const { values, initialValues, setFieldValue, isEditing } =
    useFormState<CreateSupplierOfficeInput>();
  const t = useTranslations("office.form");
  const [cityBias, setCityBias] = useState<{
    lat: number | null;
    lng: number | null;
  }>({ lat: null, lng: null });
  const [addressCoords, setAddressCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(
    initialLat != null && initialLng != null
      ? { lat: initialLat, lng: initialLng }
      : null,
  );
  const { openAsPromise: openCityCreateDialog } = useDialog(
    SupplierCityCreateDialog,
  );

  const cityToAddressDistanceKm =
    cityBias.lat != null && cityBias.lng != null && addressCoords
      ? distanceKm({ lat: cityBias.lat, lng: cityBias.lng }, addressCoords)
      : null;
  const showDistanceWarning =
    cityToAddressDistanceKm != null &&
    cityToAddressDistanceKm > MAX_OFFICE_CITY_DISTANCE_KM;

  async function selectCity(city: {
    id: string;
    name: string;
    lat: number;
    lng: number;
  }): Promise<void> {
    setCityBias({ lat: city.lat, lng: city.lng });
    if (!values.name) {
      await setFieldValue("name", city.name);
    }
    if (!values.address?.upsert?.city) {
      await setFieldValue("address.upsert.city", city.name);
    }
  }

  async function handleAddCity(): Promise<void> {
    try {
      const city = await openCityCreateDialog({});
      await setFieldValue("city.connect", city.id);
      await selectCity(city);
    } catch {
      // Dialog dismissed without creating a city.
    }
  }

  return (
    <>
      {!initialValues.supplier?.connect ? (
        <SupplierInput
          name="supplier.connect"
          label={t("supplier")}
          className="col-span-4"
        />
      ) : null}

      {values.supplier?.connect ? (
        <>
          <div className="col-span-6">
            <CityInput
              name="city.connect"
              label={t("city")}
              tooltip={t("city_tooltip")}
              onChange={async (city: CityListItem | null) => {
                if (city) {
                  await selectCity(city);
                } else {
                  setCityBias({ lat: null, lng: null });
                }
              }}
            />
            {isEditing ? (
              <Button plain className="mt-1.5 !text-sm" onClick={handleAddCity}>
                Can&apos;t find the city? Add a new one
              </Button>
            ) : null}
          </div>

          {values.city?.connect ? (
            <>
              <TextInput
                name="name"
                label={t("internal_name")}
                tooltip={t("internal_name_tooltip")}
                className="col-span-6"
              />

              <FormDivider />

              <AddressInput
                name="address.upsert"
                label={t("address")}
                className="col-span-full"
                biasLat={cityBias.lat}
                biasLng={cityBias.lng}
                initialLat={initialLat}
                initialLng={initialLng}
                onCoordsChange={setAddressCoords}
              />

              {showDistanceWarning ? (
                <div className="col-span-full flex items-start gap-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-4">
                  <TriangleAlertIcon className="mt-0.5 size-6 shrink-0 text-amber-600" />
                  <div>
                    <p className="text-base font-semibold text-amber-900">
                      Pickup address is {Math.round(cityToAddressDistanceKm)}km
                      from the advertised location.
                    </p>
                    <p className="mt-1 text-sm text-amber-800">
                      Customers see the advertised location as the pickup
                      location. If the real pickup is this far away, choose a
                      closer city or add the actual town as a new city so the
                      listing isn&apos;t misleading.
                    </p>
                  </div>
                </div>
              ) : null}

              <FormDivider />

              <TextInput
                name="code"
                label={t("code")}
                className="col-span-4"
                tooltip={t("code_tooltip")}
                optionalLabel
                inputProps={{
                  placeholder: t("code_placeholder"),
                }}
              />
              <TextInput
                name="email"
                label={t("office_email")}
                type="email"
                className="col-span-4"
                optionalLabel
              />
              <TextInput
                name="phone"
                label={t("office_phone")}
                className="col-span-4"
                optionalLabel
              />

              <FormDivider />

              <RichTextInput
                optionalLabel
                name="pickup_instructions"
                label={t("pickup_instructions")}
                className="col-span-full"
              />
            </>
          ) : null}
        </>
      ) : null}
    </>
  );
}
